import {
  AngularFirestore,
  AngularFirestoreCollection,
  CollectionReference,
  Query,
  DocumentData,
  QueryDocumentSnapshot
} from "@angular/fire/firestore";
import { Order } from "./order.model";
import { BehaviorSubject, Observable } from "rxjs";
import { environment } from "src/environments/environment";
import { ProductType } from "../product/enums";
import { map } from "rxjs/operators";
import { CitySummary } from "./models/city-summary.model";
import { VenueSummary } from "./models/venue-summary.model";
import { OrderFactoryAnalytics } from "./order.factory.analytics";
import {
  TimeframeFilter,
  TodayFilter,
  YesterdayFilter,
  WeekToDateFilter,
  Last7DaysFilter,
  LastWeekFilter,
  MonthToDateFilter,
  LastMonthFilter,
  YearToDateFilter,
  Last12MonthsFilter,
  LastCalendarYear,
  CustomTimeframeFilter,
  Last30DaysFilter
} from "../timeframe/timeframes";
import { OrderExporter } from "./order.export";
import { HttpClient } from "@angular/common/http";

export class OrderFactory {
  private collection: AngularFirestoreCollection;
  public orders$: Observable<Order[]>;
  private query: OrderQuery;
  private _cities: Map<string, CitySummary>;
  private _venues: Map<string, VenueSummary>;
  public analytics: OrderFactoryAnalytics;

  public timeframes: TimeframeFilter[] = [
    new TodayFilter(),
    new YesterdayFilter(),
    new WeekToDateFilter(),
    new Last7DaysFilter(),
    new LastWeekFilter(),
    new MonthToDateFilter(),
    new Last30DaysFilter(),
    new LastMonthFilter(),
    new YearToDateFilter(),
    new Last12MonthsFilter(),
    new LastCalendarYear(),
    new CustomTimeframeFilter(null, null)
  ];

  constructor(private db: AngularFirestore) {
    this._start();
  }

  private _start() {
    this.analytics = new OrderFactoryAnalytics();
    this._cities = new Map();
    this._venues = new Map();
    this.query = new OrderQuery();
    this._update();
  }

  private _update() {
    this.collection = this.db.collection(environment.orders, ref =>
      this.query.query(ref)
    );
    this._refresh();
  }
  public _refresh() {
    this.orders$ = this.collection.snapshotChanges().pipe<Order[]>(
      map(orders => {
        this.analytics.initialize();
        this.analytics.setTimeframe(this.query.timeframe);

        let _orders: Order[] = [];
        for (let i = 0; i < orders.length; i++) {
          let order = this._toObject(orders[i].payload.doc);
          this.analytics.addOrder(order);
          if (
            (order.summary.redeemedAt == null && this.query.showOutstanding) ||
            (order.summary.redeemedAt != null && this.query.showRedeemed)
          ) {
            _orders.push(order);
            this._cities.set(order.city.reference.id, order.city);
            this._venues.set(order.venue.reference.id, order.venue);
          }
        }
        this.query.setTotalOrders(_orders.length);
        this.analytics.next();
        return _orders;
      })
    );
  }

  public get cities(): CitySummary[] {
    return Array.from(this._cities.values());
  }

  public get venues(): VenueSummary[] {
    return Array.from(this._venues.values());
  }

  private _toObject(doc: QueryDocumentSnapshot<DocumentData>): Order {
    return new Order(doc.data(), doc.ref);
  }

  public get totalOrders(): number {
    return this.query.totalOrders;
  }

  public setCityFilter(city: CitySummary) {
    this.query.setCity(city);
    this.query.setVenue(null);
    this._update();
  }

  public setVenueFilter(venue: VenueSummary) {
    this.query.setVenue(venue);
    this.query.setCity(null);
    this._update();
  }

  public setProductTypeFilter(productType: ProductType) {
    this.query.setProduct(productType);
    this._update();
  }

  public setTimeframeFilter(timeframe: TimeframeFilter) {
    this.query.setTimeframe(timeframe);
    this._update();
  }

  public setShowRedeemed(bool: boolean) {
    this.query.setRedeemed(bool);
    this._update();
  }

  public setShowOutstanding(bool: boolean) {
    this.query.setOutstanding(bool);
    this._update();
  }

  public get currentCity(): CitySummary {
    return this.query.city;
  }

  public get currentVenue(): VenueSummary {
    return this.query.venue;
  }

  public get currentProductType(): ProductType {
    return this.query.product;
  }

  public get currentTimeframe(): TimeframeFilter {
    return this.query.timeframe;
  }

  public get showRedeemed(): boolean {
    return this.query.showRedeemed;
  }

  public get showOutstanding(): boolean {
    return this.query.showOutstanding;
  }
  public onOrderFilterSearchChanged(text: string) {
    this.query.searchText = text;
  }
  public get currentSearchText(): string {
    return this.query.searchText;
  }
  public export(orders: Order[], isAdmin: boolean): void {
    new OrderExporter(orders, this.currentTimeframe).export(isAdmin);
  }

  public exportCustomQuestions(orders: Order[], isAdmin: boolean): void {
    new OrderExporter(
      Array.from(orders.filter(order => order.user.responses.length != 0)),
      this.currentTimeframe
    ).export(isAdmin);
  }

  onAdminVenuePayoutSummaryPressed(http: HttpClient) {
    this.analytics.onAdminVenuePayoutSummaryPressed(http);
  }
  onAdminDrinkPayoutSummaryPressed(http: HttpClient) {
    this.analytics.onAdminDrinkPayoutSummaryPressed(http);
  }
}

export class OrderQuery {
  private collection: CollectionReference;
  public timeframe: TimeframeFilter;
  public city: CitySummary;
  public venue: VenueSummary;
  public product: ProductType;
  public showRedeemed: boolean;
  public showOutstanding: boolean;
  public searchText: string;
  public totalOrders: number;

  constructor() {
    this._default();
  }

  setTotalOrders(n: number) {
    this.totalOrders = n;
  }

  private _default() {
    this.timeframe = new TodayFilter();
    this.showOutstanding = true;
    this.showRedeemed = true;
    this.searchText = "";
    this.totalOrders = 0;
  }

  public setOutstanding(bool: boolean) {
    this.showOutstanding = bool;
  }

  public setRedeemed(bool: boolean) {
    this.showRedeemed = bool;
  }

  public setCity(city: CitySummary) {
    this.city = city;
  }

  public setVenue(venue: VenueSummary) {
    this.venue = venue;
  }

  public setProduct(product: ProductType) {
    this.product = product;
  }

  public setTimeframe(filter: TimeframeFilter) {
    this.timeframe = filter;
  }

  private qTimeframe(): Query {
    return this.collection
      .where("summary.purchasedAt", ">=", this.timeframe.begin)
      .where("summary.purchasedAt", "<=", this.timeframe.end);
  }

  private filterOutVIP(query:Query): Query {
    return query
      .where("vipPass", "==", null)
  }

  private qCity(query: Query): Query {
    return this.city == null
      ? query
      : query.where("city.reference", "==", this.city.reference);
  }

  private qVenue(query: Query): Query {
    return this.venue == null
      ? query
      : query.where("venue.reference", "==", this.venue.reference);
  }

  private qProduct(query: Query): Query {
    return this.product == null
      ? query
      : query.where("product.type", "==", this.product.valueOf());
  }

  private qPurchasedAt(query: Query): Query {
    return query.orderBy("summary.purchasedAt", "desc");
  }

  public query(ref: CollectionReference): Query {
    this.collection = ref;
    return this.qPurchasedAt(
      this.qProduct(this.qVenue(this.qCity(this.qTimeframe())))
    );
  }
}
